/**
 * Editor actions on the event page: roster, edit (series occurrence asks "Aðeins þessi" /
 * "Þessi og næstu"), cancel with reason / reinstate, delete event, delete series.
 */
import { useQueryClient } from '@tanstack/react-query';
import { Ban, CalendarClock, ClipboardCheck, Pencil, Repeat, RotateCcw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import type { EventDTO } from '../../../shared/dto/events';
import { findSeriesOccurrence, useCalendarMutations, useSeries } from '../../api/calendar';
import { useToast } from '../../components/toast';
import { Button, Card, CenteredSpinner, ConfirmSheet, ErrorBox, Sheet, TextInput } from '../../components/ui';
import { useI18n, useT } from '../../i18n';
import { formatDate, todayStr } from '../../lib/dates';
import { EventEditor } from './EventEditor';
import { SeriesEditor } from './SeriesEditor';
import { ChoiceButton, errorText } from './shared';

const REASON_MAX = 300;

type Step = 'idle' | 'scope' | 'edit-one' | 'edit-following' | 'cancel' | 'delete' | 'delete-series';

/** Loads the series, then shows the "this and following" editor. */
function FollowingEditor({ ev, onClose }: { ev: EventDTO; onClose: () => void }) {
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useSeries(ev.seriesId);
  const from = ev.originalDate ?? todayStr(ev.startsAt);
  if (!q.data) {
    return (
      <Sheet open onClose={onClose} title={t('calendar.form.edit_series')}>
        {q.isError ? (
          <div className="py-4">
            <ErrorBox onRetry={() => void q.refetch()} retryLabel={t('common.retry')}>
              {errorText(t, q.error)}
            </ErrorBox>
          </div>
        ) : (
          <CenteredSpinner label={t('calendar.form.loading_series')} />
        )}
      </Sheet>
    );
  }
  return (
    <SeriesEditor
      mode={{ kind: 'following', series: q.data, from, event: ev }}
      onClose={onClose}
      onSaved={(r) => {
        if (!r.newSeriesId) return;
        // The edited occurrence now belongs to the new series: open it (or go home if that day has none).
        void findSeriesOccurrence(qc, r.newSeriesId, r.from)
          .then((occ) => navigate(occ ? `/events/${occ.id}` : '/', { replace: true }))
          .catch(() => navigate('/', { replace: true }));
      }}
    />
  );
}

export function EventAdminActions({ ev }: { ev: EventDTO }) {
  const t = useT();
  const { loc } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const m = useCalendarMutations();
  const [step, setStep] = useState<Step>('idle');
  const [reason, setReason] = useState('');
  const close = () => setStep('idle');
  const cancelled = ev.status === 'cancelled';
  const isOccurrence = ev.seriesId !== null;

  const fail = (e: unknown) => toast.show(errorText(t, e), { tone: 'error' });

  return (
    <Card as="section" className="flex flex-col gap-3 p-5 md:p-6">
      <h2 className="text-[20px] font-bold">{t('calendar.actions.title')}</h2>
      <div className="grid gap-2 sm:grid-cols-2">
        <Link
          to={`/events/${ev.id}/roster`}
          className="inline-flex min-h-14 items-center justify-center gap-2 rounded-2xl bg-[var(--accent)] px-5 text-[18px] font-semibold text-[var(--accent-ink)] sm:col-span-2"
        >
          <ClipboardCheck className="size-6" aria-hidden />
          {t('calendar.actions.roster')}
        </Link>
        <Button variant="secondary" size="lg" icon={Pencil} onClick={() => setStep(isOccurrence ? 'scope' : 'edit-one')}>
          {t('calendar.actions.edit')}
        </Button>
        {cancelled ? (
          <Button
            variant="secondary"
            size="lg"
            icon={RotateCcw}
            busy={m.uncancelEvent.isPending}
            onClick={() =>
              m.uncancelEvent.mutate(ev.id, {
                onSuccess: () => toast.show(t('calendar.uncancel.done'), { tone: 'ok' }),
                onError: fail,
              })
            }
          >
            {t('calendar.actions.uncancel')}
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="lg"
            icon={Ban}
            onClick={() => {
              setReason('');
              setStep('cancel');
            }}
          >
            {t('calendar.actions.cancel')}
          </Button>
        )}
        <Button variant="ghost" size="lg" icon={Trash2} className="text-[var(--danger-text)]" onClick={() => setStep('delete')}>
          {t('calendar.actions.delete')}
        </Button>
        {isOccurrence ? (
          <Button variant="ghost" size="lg" icon={Repeat} className="text-[var(--danger-text)]" onClick={() => setStep('delete-series')}>
            {t('calendar.actions.delete_series')}
          </Button>
        ) : null}
      </div>

      <Sheet open={step === 'scope'} onClose={close} title={t('calendar.scope.title')}>
        <div className="flex flex-col gap-3 py-2">
          <ChoiceButton
            icon={CalendarClock}
            title={t('calendar.scope.only_this')}
            desc={t('calendar.scope.only_this_desc', { date: formatDate(ev.startsAt, loc) })}
            onClick={() => setStep('edit-one')}
          />
          <ChoiceButton
            icon={Repeat}
            title={t('calendar.scope.following')}
            desc={t('calendar.scope.following_desc')}
            onClick={() => setStep('edit-following')}
          />
        </div>
      </Sheet>

      {step === 'edit-one' ? <EventEditor mode={{ kind: 'edit', event: ev }} onClose={close} /> : null}
      {step === 'edit-following' ? <FollowingEditor ev={ev} onClose={close} /> : null}

      <Sheet
        open={step === 'cancel'}
        onClose={close}
        title={t('calendar.cancel.title')}
        footer={
          <>
            <Button variant="secondary" block onClick={close}>
              {t('calendar.cancel.keep')}
            </Button>
            <Button
              variant="danger"
              block
              busy={m.cancelEvent.isPending}
              onClick={() =>
                m.cancelEvent.mutate(
                  { id: ev.id, reason: reason.trim() || null },
                  {
                    onSuccess: () => {
                      close();
                      toast.show(t('calendar.cancel.done'), { tone: 'ok' });
                    },
                    onError: fail,
                  },
                )
              }
            >
              {t('calendar.cancel.confirm')}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4 py-2">
          <p className="text-[var(--ink-2)]">{t('calendar.cancel.body')}</p>
          <TextInput
            label={t('calendar.cancel.reason')}
            placeholder={t('calendar.cancel.reason_ph')}
            value={reason}
            maxLength={REASON_MAX}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </Sheet>

      <ConfirmSheet
        open={step === 'delete'}
        title={t('calendar.delete.title')}
        body={t('calendar.delete.body')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        danger
        busy={m.deleteEvent.isPending}
        onClose={close}
        onConfirm={() =>
          m.deleteEvent.mutate(ev.id, {
            onSuccess: (res) => {
              close();
              if (res?.status === 'cancelled') {
                // Had RSVPs/comments: the server cancelled it instead — stay so the editor sees it.
                toast.show(t('calendar.delete.done_cancelled'), { sticky: true });
                return;
              }
              toast.show(t('calendar.delete.done'), { tone: 'ok' });
              navigate('/', { replace: true });
            },
            onError: fail,
          })
        }
      />

      <ConfirmSheet
        open={step === 'delete-series'}
        title={t('calendar.delete_series.title')}
        body={t('calendar.delete_series.body')}
        confirmLabel={t('calendar.actions.delete_series')}
        cancelLabel={t('common.cancel')}
        danger
        busy={m.deleteSeries.isPending}
        onClose={close}
        onConfirm={() => {
          if (!ev.seriesId) return;
          m.deleteSeries.mutate(ev.seriesId, {
            onSuccess: () => {
              close();
              toast.show(t('calendar.delete_series.done'), { tone: 'ok' });
              navigate('/', { replace: true });
            },
            onError: fail,
          });
        }}
      />
    </Card>
  );
}
